import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { AudioFile } from '../services/api';
import { trackEvent } from '../services/analytics';
import { useAuth } from '../contexts/AuthContext';
import type { BannerSpec } from './banners/types';

interface ExpiringFilesBannerProps {
  expiringFiles: AudioFile[];
  count: number;
}

const STORAGE_KEY = 'dismissedExpiringFiles';

interface DismissedState {
  fileIds: string[];
  timestamp: number;
}

/**
 * Warning banner for files expiring within 24 hours
 * Shows count, CTA to upgrade, and dismissible with localStorage persistence
 */
export const ExpiringFilesBanner: React.FC<ExpiringFilesBannerProps> = ({
  expiringFiles,
  count
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const { getValidToken, hasEmailConsent } = useAuth();
  const location = useLocation();
  const shown = count > 0 && !isDismissed;
  const showMyFilesLink = location.pathname !== '/my-files';

  useEffect(() => {
    if (shown) {
      trackEvent('expiry_warning_shown', getValidToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown]);

  useEffect(() => {
    // Check if banner was previously dismissed for these specific files
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed: DismissedState = JSON.parse(stored);
        const currentFileIds = expiringFiles.map(f => f.id).sort();
        const dismissedFileIds = parsed.fileIds.sort();

        // Only keep dismissed if the exact same files are expiring
        // If new files are expiring, show banner again
        const allMatch = currentFileIds.length === dismissedFileIds.length &&
          currentFileIds.every((id, idx) => id === dismissedFileIds[idx]);

        setIsDismissed(allMatch);
      } catch (e) {
        // Invalid JSON, clear storage
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [expiringFiles]);

  const handleDismiss = () => {
    const dismissState: DismissedState = {
      fileIds: expiringFiles.map(f => f.id),
      timestamp: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissState));
    setIsDismissed(true);
  };

  // Don't render if no files or dismissed
  if (count === 0 || isDismissed) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 p-4"
      >
        <div className="flex items-start gap-3">
          {/* Warning Icon */}
          <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-400 mt-0.5" />

          {/* Content */}
          <div className="flex-1 min-w-0">
            {showMyFilesLink ? (
              <Link
                to="/my-files"
                className="block text-amber-100 text-sm hover:text-amber-50 hover:underline underline-offset-2 decoration-amber-300/60"
              >
                You have{' '}
                <span className="font-semibold">
                  {count} file{count !== 1 ? 's' : ''}
                </span>{' '}
                expiring in less than 24 hours.
              </Link>
            ) : (
              <p className="text-amber-100 text-sm">
                You have{' '}
                <span className="font-semibold">
                  {count} file{count !== 1 ? 's' : ''}
                </span>{' '}
                expiring in less than 24 hours.
              </p>
            )}
            <p className="text-amber-200/70 text-xs mt-1">
              These files disappear forever when the clock hits zero. Pro stops the clock.
            </p>
            {!hasEmailConsent() && (
              <p className="text-white/40 text-xs mt-1.5">
                Get reminded before your files expire —{' '}
                <a
                  href="/profile/notifications"
                  className="underline underline-offset-2 hover:text-white/60 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  manage email preferences
                </a>
              </p>
            )}

            {/* CTA Button */}
            <Link to="/profile/plan?utm_source=in_app_banner" onClick={() => trackEvent('upgrade_cta_clicked', getValidToken, { source: 'in_app_banner' })}>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-soundwave-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-soundwave-600"
              >
                Keep Your Files Forever
              </motion.button>
            </Link>
          </div>

          {/* Dismiss Button */}
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 rounded p-1 text-amber-400 transition-colors hover:bg-amber-500/20 hover:text-amber-300"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Nudge-tier spec: surfaces files about to expire within 24 hours so the user
 * has a chance to keep them. Beaten by the educational video card on the
 * Home top slot — the educational opportunity is rarer than the recurring
 * expiry signal, so we prefer activation over urgency. The banner itself also
 * renders elsewhere (e.g. inside /my-files), so this spec is only the
 * Home-slot wiring.
 */
export const expiringFilesBannerSpec: BannerSpec = {
  id: 'expiring-files',
  category: 'nudge',
  when: ({ expiringCount }) => expiringCount > 0,
  render: ({ expiringFiles, expiringCount }) => (
    <ExpiringFilesBanner expiringFiles={expiringFiles} count={expiringCount} />
  ),
};
